import React, { useMemo } from 'react';
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ReferenceLine,
} from 'recharts';
import { Box, Paper, Typography, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import ChartSkeleton from './ChartSkeleton';
import { corEficiencia, LIMITE_PADRAO } from '../utils/eficiencia';

function fmtNum(n) {
    if (n == null) return '—';
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
    if (n >= 1_000)     return `${(n / 1_000).toFixed(2)}k`;
    return n.toFixed(2);
}

function CustomTooltip({ active, payload, limite = LIMITE_PADRAO }) {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
        <Paper
            elevation={4}
            sx={{
                p: 1.5,
                borderRadius: 2,
                minWidth: 220,
                fontSize: 13,
                lineHeight: 1.8,
            }}
        >
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                {dayjs(d.dataHora).format('DD/MM/YYYY HH:mm')}
            </Typography>
            {d.identificacao && (
                <Box sx={{ color: 'text.secondary', mb: 0.5, fontSize: 12 }}>
                    {d.identificacao}
                </Box>
            )}
            <Box sx={{ color: corEficiencia(d.eficiencia, limite), fontWeight: 700 }}>
                Eficiência: {d.eficiencia != null ? `${d.eficiencia.toFixed(3)}%` : '—'}
            </Box>
            <Box sx={{ color: 'text.secondary', fontSize: 12, mt: 0.5 }}>
                CT: {fmtNum(d.gmCtAfluente)} → {fmtNum(d.gmCtEfluente)}
            </Box>
            <Box sx={{ color: 'text.secondary', fontSize: 12 }}>
                E.coli: {fmtNum(d.gmEcoliAfluente)} → {fmtNum(d.gmEcoliEfluente)}
            </Box>
        </Paper>
    );
}

export default function EficienciaHistoricoChart({
    data = [],
    loading = false,
    limite = LIMITE_PADRAO,
}) {
    const theme = useTheme();

    const chartData = useMemo(
        () =>
            data.map((d) => ({
                ...d,
                label: dayjs(d.dataHora).format('DD/MM HH:mm'),
            })),
        [data]
    );

    // Zoom do eixo Y: num painel de conformidade a referencia e o LIMITE, nao o zero.
    // Mostrar 0-100 desperdica a altura e esconde variacoes de poucos pontos percentuais.
    // Padrao: [limite - 10, 100] -> variacoes pequenas ficam visiveis e o limite aparece
    // perto da base. Se algum ponto cair ABAIXO desse piso (inclusive negativo), o grafico
    // faz zoom out automatico com folga, para nenhuma nao-conformidade ficar cortada.
    const yDomain = useMemo(() => {
        const valores = chartData
            .map((d) => d.eficiencia)
            .filter((v) => v != null && !Number.isNaN(v));
        const piso = Math.max(0, limite - 10);
        if (!valores.length) return [piso, 100];
        const menor = Math.min(...valores);
        // Fora do piso => abre o zoom (sem travar em 0: eficiencia pode ser negativa).
        return menor < piso ? [Math.floor(menor - 5), 100] : [piso, 100];
    }, [chartData, limite]);

    if (loading) return <ChartSkeleton height={280} />;

    return (
        <Paper
            elevation={2}
            sx={{ borderRadius: 3, p: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
        >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Eficiência do Sistema — Histórico de Análises
            </Typography>

            {chartData.length === 0 ? (
                <Box
                    sx={{
                        height: 280,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'text.secondary',
                    }}
                >
                    Nenhuma análise finalizada encontrada.
                </Box>
            ) : (
                <ResponsiveContainer width="100%" height={280}>
                    <AreaChart data={chartData} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                        <defs>
                            <linearGradient id="gradEficiencia" x1="0" y1="0" x2="0" y2="1">
                                <stop
                                    offset="5%"
                                    stopColor="#58A6FF"
                                    stopOpacity={0.35}
                                />
                                <stop
                                    offset="95%"
                                    stopColor="#58A6FF"
                                    stopOpacity={0.02}
                                />
                            </linearGradient>
                        </defs>

                        <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />

                        <XAxis
                            dataKey="label"
                            tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                            interval="preserveStartEnd"
                            tickLine={false}
                            axisLine={false}
                        />

                        <YAxis
                            domain={yDomain}
                            allowDataOverflow={false}
                            tickFormatter={(v) => `${v}%`}
                            tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                            tickLine={false}
                            axisLine={false}
                            width={48}
                        />

                        <Tooltip
                            content={(props) => (
                                <CustomTooltip {...props} limite={limite} />
                            )}
                        />

                        <ReferenceLine
                            y={limite}
                            stroke="#4caf50"
                            strokeDasharray="6 3"
                            label={{
                                value: `Limite ${limite}%`,
                                position: 'insideTopRight',
                                fill: '#4caf50',
                                fontSize: 11,
                            }}
                        />

                        <Area
                            type="monotone"
                            dataKey="eficiencia"
                            stroke="#58A6FF"
                            strokeWidth={2}
                            fill="url(#gradEficiencia)"
                            dot={(props) => {
                                const { cx, cy, payload } = props;
                                return (
                                    <circle
                                        key={payload.dataHora}
                                        cx={cx}
                                        cy={cy}
                                        r={4}
                                        fill={corEficiencia(payload.eficiencia, limite)}
                                        stroke="#fff"
                                        strokeWidth={1.5}
                                    />
                                );
                            }}
                            activeDot={{ r: 6 }}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            )}
        </Paper>
    );
}
